import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useParams, useHistory } from "react-router-dom";
import {
  Grid,
  Paper,
  Typography,
  Divider,
  Box,
  Tooltip,
  IconButton,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  useTheme,
  alpha,
  Badge,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import {
  getImportItemById,
  getImportStockUpdateByImportId,
} from "../../../services/Import";
import TableItem from "../../../components/TableItem";
import CloseIcon from "@mui/icons-material/Close";

// Icons
import ReceiptIcon from "@mui/icons-material/Receipt";
import CategoryIcon from "@mui/icons-material/Category";
import InventoryIcon from "@mui/icons-material/Inventory";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import StorefrontIcon from "@mui/icons-material/Storefront";
import DescriptionIcon from "@mui/icons-material/Description";
import PersonIcon from "@mui/icons-material/Person";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CodeIcon from "@mui/icons-material/Code";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import NoteAltIcon from "@mui/icons-material/NoteAlt";

function Im_ItemView() {
  const theme = useTheme();
  const history = useHistory();
  const { importId } = useParams();
  const [importItem, setImportItem] = useState(null);
  const [rows, setRows] = useState([]);
  const [isLoading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch import item details
      const result = await getImportItemById(importId);
      if (result) {
        setImportItem(result);

        // Fetch stock updates
        const stockUpdateSet = await getImportStockUpdateByImportId(importId);

        if (stockUpdateSet) {
          // Transform data for table display
          const newSet = stockUpdateSet.map((item) => ({
            date: item.addedDate,
            item: item.imports.itemName,
            brand: item.imports.brand,
            billNo: item.billNo,
            qty: item.qty,
            total: item.totalPrice,
            id: item.intakeId,
          }));

          setRows(newSet);
        }
      } else {
        setErrors({
          ...errors,
          _importItemError: "Unable to fetch import item data",
        });
      }
    } catch (error) {
      console.error("Error fetching import data:", error);
      setErrors({
        ...errors,
        _fetchError: error.message || "An error occurred while fetching data",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [importId]);

  // Table columns definition
  const columns = [
    {
      id: "date",
      label: "Date",
      minWidth: 100,
    },
    {
      id: "item",
      label: "Item",
      minWidth: 150,
    },
    {
      id: "brand",
      label: "Brand",
      minWidth: 120,
    },
    {
      id: "billNo",
      label: "Bill No",
      minWidth: 100,
    },
    {
      id: "qty",
      label: "Quantity",
      minWidth: 80,
      type: "number",
      format: (value) => value.toLocaleString("en-US"),
    },
    {
      id: "total",
      label: "Total",
      minWidth: 120,
      type: "number",
      format: (value) => `Rs: ${parseFloat(value).toFixed(2)}`,
    },
  ];

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "70vh",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography color="text.secondary">
          Loading import item details...
        </Typography>
      </Box>
    );
  }

  if (!importItem) {
    return (
      <Paper
        elevation={3}
        sx={{
          p: 4,
          m: 3,
          textAlign: "center",
          borderRadius: 2,
          backgroundColor: alpha(theme.palette.error.light, 0.1),
        }}
      >
        <Box sx={{ mb: 2 }}>
          <DescriptionIcon color="error" sx={{ fontSize: 48 }} />
        </Box>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Import Item Not Found
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          The requested import item data could not be retrieved or does not
          exist.
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => history.goBack()}
        >
          Go Back
        </Button>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={3}
      sx={{
        p: { xs: 2, md: 2 },
        m: { xs: 1, md: 2 },
        borderRadius: 2,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <InventoryIcon fontSize="large" color="primary" />
          <Typography variant="h5" fontWeight="600" color="primary.main">
            Import Item Details
          </Typography>
        </Box>
        <Tooltip title="Close">
          <IconButton
            onClick={() => history.goBack()}
            aria-label="close"
            sx={{
              bgcolor: alpha(theme.palette.grey[500], 0.1),
              "&:hover": {
                bgcolor: alpha(theme.palette.grey[500], 0.2),
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Divider sx={{ mb: 4 }} />

      {/* Item Information */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <Card elevation={1} sx={{ height: "100%", borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <InventoryIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography
                  variant="subtitle1"
                  fontWeight="700"
                  color="primary.main"
                >
                  Item Information
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Item Name
                  </Typography>
                  <Typography variant="body1" fontWeight="600">
                    {importItem.itemName}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Brand
                  </Typography>
                  <Typography variant="body1">{importItem.brand}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Product Code
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <CodeIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                    <Typography variant="body1">
                      {importItem.product_code}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Unit Type
                  </Typography>
                  <Typography variant="body1">{importItem.unitType}</Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card elevation={1} sx={{ height: "100%", borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <PersonIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography
                  variant="subtitle1"
                  fontWeight="700"
                  color="primary.main"
                >
                  Added Information
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Added By
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <PersonIcon
                      sx={{ fontSize: 16, color: "text.secondary" }}
                    />
                    <Typography variant="body1">
                      {importItem.addedBy}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Added Date
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <CalendarTodayIcon
                      sx={{ fontSize: 16, color: "text.secondary" }}
                    />
                    <Typography variant="body1">
                      {importItem.addedDate}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Inventory Information */}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <Card elevation={1} sx={{ borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <StorefrontIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography
                  variant="subtitle1"
                  fontWeight="700"
                  color="primary.main"
                >
                  Inventory Status
                </Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      bgcolor: alpha(theme.palette.success.main, 0.1),
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Current Available Qty
                    </Typography>
                    <Typography
                      variant="h6"
                      fontWeight="700"
                      color="success.main"
                    >
                      {importItem.qty || 0}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      bgcolor: alpha(theme.palette.info.main, 0.1),
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Refill Rate
                    </Typography>
                    <Typography
                      variant="h6"
                      fontWeight="700"
                      color="primary.main"
                    >
                      {importItem.refillRate || 0}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card elevation={1} sx={{ height: "100%", borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <CategoryIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography
                  variant="subtitle1"
                  fontWeight="700"
                  color="primary.main"
                >
                  Categories
                </Typography>
              </Box>

              {importItem.im_categories &&
              importItem.im_categories.length > 0 ? (
                <Stack
                  direction="row"
                  spacing={1}
                  flexWrap="wrap"
                  sx={{ gap: 1 }}
                >
                  {importItem.im_categories.map((category, index) => (
                    <Chip
                      key={index}
                      label={
                        <Typography variant="body2">
                          <span style={{ fontWeight: "600" }}>
                            {category.category}
                          </span>
                          : {category.subCat_1}
                        </Typography>
                      }
                      sx={{
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        mb: 1,
                      }}
                    />
                  ))}
                </Stack>
              ) : (
                <Box
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    bgcolor: alpha(theme.palette.grey[500], 0.05),
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                  }}
                >
                  <Typography color="text.secondary">
                    No categories assigned to this item
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Item Note */}
      {importItem.note && (
        <Card elevation={1} sx={{ mb: 4, borderRadius: 2 }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <NoteAltIcon sx={{ mr: 1, color: "primary.main" }} />
              <Typography
                variant="subtitle1"
                fontWeight="700"
                color="primary.main"
              >
                Note
              </Typography>
            </Box>
            <Typography variant="body2" sx={{ pl: 3, mt: 1 }}>
              {importItem.note}
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Stock Updates Table */}
      <Card elevation={1} sx={{ mb: 4, borderRadius: 2, overflow: "hidden" }}>
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <LocalShippingIcon sx={{ mr: 1, color: "primary.main" }} />
            <Typography
              variant="subtitle1"
              fontWeight="700"
              color="primary.main"
            >
              Stock Update History
            </Typography>
            <Badge badgeContent={rows.length} color="primary" sx={{ ml: 1 }} />
          </Box>

          {rows.length > 0 ? (
            <Box
              sx={{
                bgcolor: alpha(theme.palette.background.default, 0.5),
                borderRadius: 1,
                overflow: "hidden",
              }}
            >
              <TableItem
                columns={columns}
                rows={rows}
                page={page}
                setPage={setPage}
                tablePagin={true}
                totalPages={totalPages}
                getData={fetchData}
              />
            </Box>
          ) : (
            <Box
              sx={{
                p: 3,
                borderRadius: 2,
                bgcolor: alpha(theme.palette.grey[500], 0.05),
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <UnfoldMoreIcon sx={{ color: "text.secondary", fontSize: 32 }} />
              <Typography color="text.secondary">
                No stock update history available for this item
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => history.goBack()}
        >
          Go Back
        </Button>
      </Box>
    </Paper>
  );
}

Im_ItemView.propTypes = {
  importId: PropTypes.string,
};

export default Im_ItemView;
